import { useMemo } from 'react';

import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { css } from '@emotion/react';
import { Paper, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { Image } from '@scorenco/components';
import { Types } from '@scorenco/core';

type GetSponsorsData = {
  sponsors_sponsor: Types.Sponsors_Sponsor[];
};

const shuffleSponsors = (partnersArray: Types.Sponsors_Sponsor[]) => {
  partnersArray.sort(() => Math.random() - 0.5);
};

const PartnersContent = ({
  sponsors,
  tournamentSponsors,
}: {
  sponsors: Types.Sponsors_Sponsor[];
  tournamentSponsors: Types.Tournaments_Tournament_Ad[];
}) => {
  const partners = useMemo(() => {
    const shuffledSponsors = [...sponsors];
    shuffleSponsors(shuffledSponsors);
    return {
      major: shuffledSponsors?.filter(({ is_major }) => !!is_major) || [],
      second: shuffledSponsors?.filter(({ is_major }) => !is_major) || [],
    };
  }, [sponsors]);

  const style = {
    root: css`
      max-width: 100vw;
      display: flex;
      justify-content: center;
      flex-direction: column;
      height: 100vh;

      .partners-container {
        display: flex;
        flex-direction: row;
        justify-content: center;
        overflow: hidden;

        .partner {
          width: 25rem;
          min-width: 25rem;
          height: 25rem;
          padding: 1rem;
          margin: 1rem;
          display: flex;
          align-items: center;
          justify-content: center;

          img {
            object-fit: contain;
            width: 90%;
            height: 90%;
          }
        }

        .second {
          width: ${partners.major.length > 0 ? '22rem' : '30rem'};
          min-width: ${partners.major.length > 0 ? '22rem' : '30rem'};
          height: ${partners.major.length > 0 ? '22rem' : '30rem'};
        }
      }
      .tenPartners {
        justify-content: flex-start;
      }

      .majorPartnerSlider {
        animation: partnerSlider 20s linear infinite;
      }

      .partnerSlider {
        animation: partnerSlider 10s linear infinite;
      }

      @keyframes partnerSlider {
        0% {
          transform: translateX(0);
        }
        100% {
          transform: translateX(-100vw);
        }
      }
    `,
  };

  return (
    <Box css={style.root}>
      {partners.major.length > 0 && (
        <Box
          className={
            partners.major?.length > 4
              ? 'partners-container partners-container-major tenPartners'
              : 'partners-container partners-container-major'
          }
        >
          {partners.major?.map((partner, idx) => (
            <Paper
              key={idx}
              variant="bordered"
              className={
                partners.major?.length > 4
                  ? 'partner majorPartnerSlider'
                  : 'partner'
              }
            >
              <Image src={partner.logo} />
            </Paper>
          ))}
        </Box>
      )}
      <Box
        className={
          partners.second.length > 10
            ? 'partners-container tenPartners'
            : 'partners-container'
        }
      >
        {partners.second?.map((partner, idx) => (
          <Paper
            key={idx + 200}
            variant="bordered"
            className={
              partners.second?.length > 5
                ? 'partner second partnerSlider'
                : 'partner second'
            }
          >
            <Image src={partner.logo} />
          </Paper>
        ))}
      </Box>

      <Box
        className={
          tournamentSponsors?.length > 10
            ? 'partners-container tenPartners'
            : 'partners-container'
        }
      >
        {tournamentSponsors?.length > 0 &&
          tournamentSponsors.map((partner, idx) => (
            <Paper
              key={`${partner.id}-${idx}`}
              variant="bordered"
              className={
                tournamentSponsors?.length > 5
                  ? 'partner second majorPartnerSlider'
                  : 'partner second'
              }
            >
              <Image src={partner.image} />
            </Paper>
          ))}
        <Box
          className={
            tournamentSponsors?.length > 10
              ? 'partners-container tenPartners'
              : 'partners-container'
          }
        >
          {tournamentSponsors?.length < 20 &&
            tournamentSponsors?.length > 5 &&
            tournamentSponsors?.map((partner, idx) => (
              <Paper
                key={`${partner.id}-${idx}`}
                variant="bordered"
                className={
                  tournamentSponsors?.length > 5
                    ? 'partner second partnerSlider'
                    : 'partner second'
                }
              >
                <Image src={partner.image} />
              </Paper>
            ))}
        </Box>
      </Box>
    </Box>
  );
};

export const Partners = ({
  clubHouse,
  screen,
}: {
  clubHouse: Types.Clubhouse_Club_House;
  screen: Types.Clubhouse_Screen;
}) => {
  const clubId = clubHouse.club?.id;
  const getPartnersByCategoryQuery = useQuery<GetSponsorsData>(
    gql`
      query Partners_GetPartnersByCategory(
        $clubId: Int = 111402
        $sponsorsCategory: Int!
      ) {
        sponsors_sponsor(
          where: {
            club_id: { _eq: $clubId }
            category_id: { _eq: $sponsorsCategory }
          }
        ) {
          name
          logo
          is_major
        }
      }
    `,
    {
      variables: {
        clubId: clubId ?? 0,
        sponsorsCategory: screen?.sponsors_category_id,
      },
      skip: !clubId || !screen?.sponsors_category_id,
      context: {
        role: 'anonymous',
      },
    }
  );
  const getPartnersQuery = useQuery<GetSponsorsData>(
    gql`
      query Partners_GetPartners($clubId: Int) {
        sponsors_sponsor(where: { club_id: { _eq: $clubId } }) {
          name
          logo
          is_major
        }
      }
    `,
    {
      variables: {
        clubId: clubId ?? 0,
      },
      skip: !!screen?.sponsors_category_id || !clubId,
      context: {
        role: 'anonymous',
      },
    }
  );

  const sponsors =
    (screen?.sponsors_category_id
      ? getPartnersByCategoryQuery.data?.sponsors_sponsor
      : getPartnersQuery.data?.sponsors_sponsor) ?? [];

  const tournamentSponsors = screen?.tournament?.tournament_ads ?? [];

  if (getPartnersByCategoryQuery.loading || getPartnersQuery.loading) {
    return <Typography>Chargement des sponsors...</Typography>;
  }

  return (
    <PartnersContent
      sponsors={sponsors}
      tournamentSponsors={tournamentSponsors}
    />
  );
};
